<template>
  <Teleport v-if="visible" to="body">
    <div @click="hideModal">
      <transition name="fade" appear>
        <div
          v-if="visible"
          :id="uniqueId"
          :class="{
            modal: true,
          }"
          :style="{ zIndex: modalZIndex }"
          role="dialog"
          aria-labelledby="custom-modal"
          aria-hidden="true"
        >
          <div
            :class="{
              'modal-dialog': true,
              'modal-dialog-centered': centered,
              'modal-dialog-scrollable': scrollable,

              'modal-md': size == 'md',
              'modal-lg': size == 'lg',
              'modal-xl': size == 'xl',
              'modal-xxl': size == 'xxl',
              'modal-sm': size == 'sm',
              'modal-full': size == 'full',
            }"
          >
            <div :class="{ 'modal-content': true, 'shadow-lg': true, 'rounded-0': size == 'full' }">
              <!-- Modal Header -->
              <div v-if="!hideHeader" :class="{ 'modal-header': true, [headerClass]: headerClass }">
                <slot name="modal-header">
                  <slot name="modal-title">
                    <h5 v-if="title" id="custom-modal" class="modal-title">
                      {{ title }}
                    </h5>
                  </slot>
                  <button type="button" class="close" aria-label="Close" @click="hide">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </slot>
              </div>

              <!-- Modal Body -->
              <div :class="{ 'modal-body': true, 'modal-scroll': scrollable ? true : false, [bodyClass]: true }">
                <slot />
              </div>

              <!-- Modal Footer -->
              <div v-if="!hideFooter" class="modal-footer">
                <slot name="modal-footer">
                  <button type="button" class="btn btn-secondary" @click="hide">Close</button>
                  <!-- Additional buttons or actions can be added here -->
                </slot>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <div v-if="visible && !hideBackdrop" class="modal-backdrop show" :style="{ zIndex: backdropZIndex }" />
    </div>
  </Teleport>
</template>

<script>
export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    bodyClass: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg", "xl", "xxl", "full"].includes(value),
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    headerClass: {
      type: String,
      default: "",
    },
    hideBackdrop: {
      type: Boolean,
      default: false,
    },
    centered: {
      type: Boolean,
      default: false,
    },
    scrollable: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "",
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["hide"],
  data() {
    return {
      uniqueId: `modal-${Math.random().toString(36).substring(7)}`,
      preExistingModalOpen: false,
      modalZIndex: 1050,
      backdropZIndex: 1040,
    };
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        // Set z-indices before showing the modal
        this.setZIndices();

        if (document.body.classList.contains("modal-open")) {
          this.preExistingModalOpen = true;
        } else {
          document.body.classList.add("modal-open");
          this.preExistingModalOpen = false;
        }
      } else if (!this.preExistingModalOpen) {
        document.body.classList.remove("modal-open");
      }
    },
  },
  created() {
    // Set z-indices as early as possible
    this.setZIndices();
  },

  mounted() {
    if (this.visible) {
      document.body.classList.add("modal-open");
    }
  },
  beforeUnmount() {
    // Clean up modal-open class if component is unmounted while visible
    if (this.visible && !this.preExistingModalOpen) {
      document.body.classList.remove("modal-open");
    }
  },
  methods: {
    hide() {
      this.$emit("hide", true);
    },
    hideModal(event) {
      if (this.noCloseOnBackdrop) {
        return;
      }
      if (event.target.id === this.uniqueId) {
        this.hide();
      }
    },
    setZIndices() {
      // Get all modals and backdrops on the page
      const modals = document.querySelectorAll(".modal");
      const backdrops = document.querySelectorAll(".modal-backdrop");

      // Find the highest z-index used by any element
      let highestZIndex = 1050; // Bootstrap default starting point

      // Check both modals and backdrops for highest z-index
      [...modals, ...backdrops].forEach((element) => {
        const zIndex = parseInt(window.getComputedStyle(element).zIndex, 10);
        if (!Number.isNaN(zIndex) && zIndex > highestZIndex) {
          highestZIndex = zIndex;
        }
      });

      // Set z-indices with proper incrementing to ensure stacking
      // For nested modals: parent modal < child backdrop < child modal
      this.backdropZIndex = highestZIndex + 10;
      this.modalZIndex = highestZIndex + 20;

      // For debugging
      // console.log(`Modal ${this.uniqueId} - Modal: ${this.modalZIndex}, Backdrop: ${this.backdropZIndex}`);
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Add styling to ensure modals stack properly */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
}

/* No default z-index here - we'll calculate it dynamically */

/* Ensure modal has a higher default z-index */
.modal {
  z-index: 1050; /* Default bootstrap z-index */
}
</style>
